import React, { useState, useCallback, useEffect } from "react";
import {
  Button,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Label,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Row,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import { APIClient } from "../../../helpers/api_helper";

const apiClient = new APIClient();

const ModalStatus = ({
  operacao,
  setOperacao,
  setmodalStatus,
  modalStatus,
  setShouldFetch,
  formatCurrency,
}) => {
  const [loading, setLoading] = useState(false);
  const [novoStatus, setNovoStatus] = useState(operacao?.status);

  const mudarStatus = useCallback(
    async (operacaoId) => {
      setLoading(true);
      const response = await apiClient.put(`/operacao/${operacaoId}`, {
        status: novoStatus,
      });

      if (response.sucesso) {
        toast.success(response.mensagem);
        const novaOperacao = { ...operacao };
        novaOperacao.status = novoStatus;
        setOperacao(novaOperacao);
        setmodalStatus(false);
        setShouldFetch(true);
      } else {
        toast.error(response.mensagem);
      }
      setLoading(false);
    },
    [setShouldFetch, novoStatus, setmodalStatus, operacao, setOperacao]
  );

  useEffect(() => {
    if (!operacao) return;
    if (!novoStatus) {
      setNovoStatus(operacao?.status);
    }
  }, [novoStatus, operacao]);

  // AIDEV-NOTE: Status badge color mapping for better visual clarity
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "completada":
        return "success";
      case "aguardando":
        return "warning";
      case "estornada":
        return "info";
      case "cancelada":
        return "danger";
      default:
        return "secondary";
    }
  };

  // AIDEV-NOTE: Direction badge color mapping
  const getDirecaoBadgeColor = (direcao) => {
    switch (direcao) {
      case "compra":
        return "success";
      case "venda":
        return "danger";
      default:
        return "dark";
    }
  };

  // AIDEV-NOTE: Nota fiscal badge color and text mapping
  const getNotaBadgeInfo = (emitirNota, notaId) => {
    if (notaId && notaId > 0) {
      return {
        color: "success",
        text: `Nota Emitida (ID: ${notaId})`,
        icon: "check-circle"
      };
    } else if (emitirNota) {
      return {
        color: "warning",
        text: "Aguardando Emissão",
        icon: "clock"
      };
    } else {
      return {
        color: "secondary",
        text: "Sem Nota Fiscal",
        icon: "x-circle"
      };
    }
  };

  return (
    <Modal
      size="lg"
      isOpen={modalStatus}
      backdrop={"static"}
      centered
      toggle={() => {
        setmodalStatus(false);
      }}
    >
      <ModalHeader
        className="modal-title"
        toggle={() => {
          setmodalStatus(false);
        }}
      >
        Dados da Operação
      </ModalHeader>
      <ModalBody>
        {/* Main Operation Details Section */}
        <Card className="mb-4">
          <CardHeader>
            <h5 className="card-title mb-0">Detalhes da Operação Principal</h5>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md={6} className="mb-3">
                <Label className="fw-bold">Cliente:</Label>
                <p className="mb-0">{operacao?.user?.name || "N/A"}</p>
              </Col>
              <Col md={6} className="mb-3">
                <Label className="fw-bold">Direção:</Label>
                <div>
                  <Badge
                    color={getDirecaoBadgeColor(operacao?.direcao)}
                    className="ms-1"
                  >
                    {operacao?.direcao?.charAt(0)?.toUpperCase() +
                      operacao?.direcao?.slice(1) || "N/A"}
                  </Badge>
                </div>
              </Col>
              <Col md={6} className="mb-3">
                <Label className="fw-bold">Moeda:</Label>
                <p className="mb-0">
                  {operacao?.moeda
                    ? `${operacao.moeda} ${
                        operacao?.rede?.nome ? `(${operacao.rede.nome})` : ""
                      }`
                    : "N/A"}
                </p>
              </Col>
              <Col md={6} className="mb-3">
                <Label className="fw-bold">Quantidade:</Label>
                <p className="mb-0">
                  {operacao?.quantidade
                    ? `${parseFloat(operacao.quantidade).toFixed(6)} ${
                        operacao?.moeda || ""
                      }`
                    : "N/A"}
                </p>
              </Col>
              <Col md={6} className="mb-3">
                <Label className="fw-bold">Total (BRL):</Label>
                <p className="mb-0">
                  {operacao?.total_brl
                    ? formatCurrency(operacao.total_brl)
                    : "N/A"}
                </p>
              </Col>
              <Col md={6} className="mb-3">
                <Label className="fw-bold">Taxa Fort:</Label>
                <p className="mb-0">
                  {operacao?.taxa_fort
                    ? formatCurrency(operacao.taxa_fort)
                    : "N/A"}
                </p>
              </Col>
              <Col md={6} className="mb-3">
                <Label className="fw-bold">Taxa Fort (%):</Label>
                <p className="mb-0">
                  {operacao?.taxa_fort_percentage
                    ? `${parseFloat(operacao.taxa_fort_percentage).toFixed(3)}%`
                    : "0,000%"}
                </p>
              </Col>
              <Col md={6} className="mb-3">
                <Label className="fw-bold">Taxa Rede:</Label>
                <p className="mb-0">
                  {operacao?.taxa_rede_brl
                    ? formatCurrency(operacao.taxa_rede_brl)
                    : "N/A"}
                </p>
              </Col>
              <Col md={6} className="mb-3">
                <Label className="fw-bold">Cotação:</Label>
                <p className="mb-0">
                  {operacao?.cotacao
                    ? new Intl.NumberFormat("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 8,
                      }).format(operacao.cotacao)
                    : "0,00"}
                </p>
              </Col>
              <Col md={6} className="mb-3">
                <Label className="fw-bold">Data de Criação:</Label>
                <p className="mb-0">
                  {operacao?.created_at
                    ? new Date(operacao.created_at).toLocaleString("pt-BR")
                    : "N/A"}
                </p>
              </Col>
              <Col md={6} className="mb-3">
                <Label className="fw-bold">Status:</Label>
                <div>
                  <Badge
                    color={getStatusBadgeColor(operacao?.status)}
                    className="ms-1"
                  >
                    {operacao?.status?.charAt(0)?.toUpperCase() +
                      operacao?.status?.slice(1) || "N/A"}
                  </Badge>
                </div>
              </Col>
              <Col md={6} className="mb-3">
                <Label className="fw-bold">Nota Fiscal:</Label>
                <div>
                  <Badge
                    color={getNotaBadgeInfo(operacao?.emitir_nota, operacao?.nota_id).color}
                    className="ms-1"
                  >
                    {getNotaBadgeInfo(operacao?.emitir_nota, operacao?.nota_id).text}
                  </Badge>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Sub-Operations Section */}
        <Card className="mb-4">
          <CardHeader>
            <h5 className="card-title mb-0">Sub-Operações</h5>
          </CardHeader>
          <CardBody className="p-0">
            {operacao?.sub_operacoes && operacao.sub_operacoes.length > 0 ? (
              <Table className="table-sm mb-0" responsive>
                <thead className="bg-light">
                  <tr>
                    <th style={{ fontSize: "12px", padding: "8px" }}>
                      Total (BRL)
                    </th>
                    <th style={{ fontSize: "12px", padding: "8px" }}>
                      Quantidade
                    </th>
                    <th style={{ fontSize: "12px", padding: "8px" }}>
                      Fornecedor
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {operacao.sub_operacoes.map((subOp, index) => (
                    <tr key={index}>
                      <td style={{ fontSize: "12px", padding: "8px" }}>
                        {subOp?.total_brl
                          ? formatCurrency(subOp.total_brl)
                          : "N/A"}
                      </td>
                      <td style={{ fontSize: "12px", padding: "8px" }}>
                        {operacao?.direcao === "compra" ? (
                          <Badge color="danger" className="me-1">
                            Venda
                          </Badge>
                        ) : (
                          <Badge color="success" className="me-1">
                            Compra
                          </Badge>
                        )}
                        {subOp?.quantidade
                          ? `${parseFloat(subOp.quantidade).toFixed(6)} ${
                              operacao?.moeda || ""
                            }`
                          : "N/A"}
                      </td>
                      <td style={{ fontSize: "12px", padding: "8px" }}>
                        {subOp?.fornecedor ? (
                          <span>{subOp.fornecedor.nome}</span>
                        ) : subOp?.sub_op_id ? (
                          <span>
                            <Badge color="primary" className="me-1">
                              Op. Vinculada
                            </Badge>
                            ID: {subOp.sub_op_id}
                          </span>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="p-3">
                <p className="text-muted text-center mb-0">
                  Nenhuma sub-operação encontrada
                </p>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Status Change Section */}
        <Card>
          <CardHeader>
            <h5 className="card-title mb-0">Alterar Status</h5>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md={8}>
                <Label className="form-Label">Novo Status</Label>
                <Input
                  type="select"
                  className="form-control"
                  value={novoStatus}
                  onChange={(e) => setNovoStatus(e.target.value)}
                  disabled={operacao?.status === "completada"}
                >
                  <option value="aguardando">Aguardando</option>
                  <option value="completada">Completada</option>
                  <option value="estornada">Estornada</option>
                  <option value="cancelada">Cancelada</option>
                </Input>
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <Button
                  color="primary"
                  className="w-100"
                  onClick={() => {
                    mudarStatus(operacao?.id);
                  }}
                  disabled={loading || operacao?.status === novoStatus}
                >
                  {loading ? "Atualizando..." : "Atualizar"}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </ModalBody>
    </Modal>
  );
};

export default ModalStatus;
